import React, { useEffect, useState } from "react";
import { AiOutlineShop, AiOutlineSafety } from "react-icons/ai";
import { MdBlock, MdCheckCircle, MdCancel } from "react-icons/md";
import {
  FiSearch,
  FiTrash2,
  FiShield,
  FiShieldOff,
  FiDownload,
  FiInfo,
  FiDollarSign,
  FiAlertCircle,
  FiMapPin,
  FiPhone,
  FiMail,
  FiCalendar,
  FiGlobe,
  FiFileText,
} from "react-icons/fi";
import { RxCross1 } from "react-icons/rx";
import { BiBlock } from "react-icons/bi";
import { SiMicrosoftexcel } from "react-icons/si";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import styles from "../../styles/styles";
import Loader from "../Layout/Loader";
import * as XLSX from "xlsx";
import { getAvatarUrl } from "../../utils/mediaUtils";
import { useCurrency } from "../../context/CurrencyContext";

const AllSellers = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    banned: 0,
    pending: 0,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsModalAnimating, setDetailsModalAnimating] = useState(false);

  const [banModalOpen, setBanModalOpen] = useState(false);
  const [banModalAnimating, setBanModalAnimating] = useState(false);
  const [banReason, setBanReason] = useState("");

  const [unbanModalOpen, setUnbanModalOpen] = useState(false);
  const [unbanModalAnimating, setUnbanModalAnimating] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sellerIdToDelete, setSellerIdToDelete] = useState("");

  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawLoading, setWithdrawLoading] = useState(false);

  const { formatPrice } = useCurrency();

  const fetchSellers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${server}/shop/admin-all-sellers?page=${currentPage}&limit=12`,
        { withCredentials: true }
      );
      if (response.data.success) {
        setSellers(response.data.sellers || []);
        setTotalPages(response.data.totalPages || 1);
        setStats({
          total: response.data.totalSellers || 0,
          active: response.data.activeSellers || 0,
          banned: response.data.bannedSellers || 0,
          pending: response.data.pendingSellers || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching sellers:", error);
      toast.error(error.response?.data?.message || "Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSellers();
  }, [currentPage]);

  const openDetailsModal = (seller) => {
    setSelectedSeller(seller);
    setDetailsModalOpen(true);
    setTimeout(() => setDetailsModalAnimating(true), 10);
  };

  const closeDetailsModal = () => {
    setDetailsModalAnimating(false);
    setTimeout(() => {
      setDetailsModalOpen(false);
      setSelectedSeller(null);
    }, 300);
  };

  const openBanModal = (seller) => {
    setSelectedSeller(seller);
    setBanModalOpen(true);
    setTimeout(() => setBanModalAnimating(true), 10);
  };

  const closeBanModal = () => {
    setBanModalAnimating(false);
    setTimeout(() => {
      setBanModalOpen(false);
      setBanReason("");
    }, 300);
  };

  const openUnbanModal = (seller) => {
    setSelectedSeller(seller);
    setUnbanModalOpen(true);
    setTimeout(() => setUnbanModalAnimating(true), 10);
  };

  const closeUnbanModal = () => {
    setUnbanModalAnimating(false);
    setTimeout(() => {
      setUnbanModalOpen(false);
    }, 300);
  };

  const openDeleteModal = (id) => {
    setSellerIdToDelete(id);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSellerIdToDelete("");
  };

  const handleAdminWithdraw = async (e) => {
    e.preventDefault();
    if (!withdrawAmount || Number(withdrawAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (Number(withdrawAmount) > selectedSeller.dynamicAvailableBalance) {
      toast.error("Amount exceeds available balance");
      return;
    }

    try {
      setWithdrawLoading(true);
      const response = await axios.post(
        `${server}/withdraw/admin-create-withdraw-request`,
        { sellerId: selectedSeller._id, amount: Number(withdrawAmount) },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Manual payout recorded successfully!");
        setWithdrawModalOpen(false);
        setWithdrawAmount("");
        fetchSellers();
        // Update the local selected seller to reflect new balance
        setSelectedSeller({
          ...selectedSeller,
          dynamicAvailableBalance: selectedSeller.dynamicAvailableBalance - Number(withdrawAmount)
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to process withdrawal");
    } finally {
      setWithdrawLoading(false);
    }
  };

  const handleBanSeller = async () => {
    if (!banReason.trim()) {
      toast.error("Please provide a reason for banning this vendor");
      return;
    }

    try {
      const response = await axios.put(
        `${server}/shop/ban-shop`,
        { shopId: selectedSeller._id, banReason },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Vendor banned successfully");
        closeBanModal();
        fetchSellers();
      }
    } catch (error) {
      console.error("Ban vendor error:", error);
      toast.error(error.response?.data?.message || "Error banning vendor");
    }
  };

  const handleUnbanSeller = async () => {
    try {
      const response = await axios.put(
        `${server}/shop/unban-shop`,
        { shopId: selectedSeller._id },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Vendor unbanned successfully");
        closeUnbanModal();
        fetchSellers();
      }
    } catch (error) {
      console.error("Unban vendor error:", error);
      toast.error(error.response?.data?.message || "Error unbanning vendor");
    }
  };

  const handleDeleteSeller = async () => {
    try {
      const response = await axios.delete(
        `${server}/shop/delete-seller/${sellerIdToDelete}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Vendor and associated data deleted");
        closeDeleteModal();
        fetchSellers();
      }
    } catch (error) {
      console.error("Delete vendor error:", error);
      toast.error(error.response?.data?.message || "Error deleting vendor");
    }
  };

  const exportToExcel = async () => {
    try {
      toast.info("Fetching all vendor data for export... Please wait");

      const response = await axios.get(
        `${server}/shop/admin-all-sellers`,
        { withCredentials: true }
      );
      const allSellers = response.data.sellers || [];

      if (allSellers.length === 0) {
        toast.error("No vendors to export");
        return;
      }

      // 1. Sellers Overview
      const sellersData = allSellers.map((seller, index) => ({
        "S.No": index + 1,
        "Shop ID": seller._id,
        "Shop Name": seller.name,
        "Email Address": seller.email,
        "Phone Number": seller.phoneNumber || "N/A",
        "Business Name": seller.businessName || "N/A",
        "Available Balance": `$${(seller.availableBalance || 0).toFixed(2)}`,
        "Approval Status": seller.approvalStatus || "pending",
        Status: seller.isBanned ? "Banned" : "Active",
        "Ban Reason": seller.banReason || "N/A",
        Address: seller.address || "N/A",
        City: seller.city || "N/A",
        State: seller.state || "N/A",
        Country: seller.country || "India",
        "Zip Code": seller.zipCode || "N/A",
        "Created Date": seller.createdAt
          ? new Date(seller.createdAt).toLocaleDateString()
          : "N/A",
      }));

      // 2. Bank Details Sheet
      const bankDetailsData = allSellers
        .filter((seller) => seller.withdrawMethod || seller.bankAccountDetails || seller.paypalEmail)
        .map((seller, index) => {
          const method = seller.withdrawMethod || {};
          const bank = seller.bankAccountDetails || {};
          return {
            "S.No": index + 1,
            "Shop Name": seller.name,
            "Email Address": seller.email,
            "UPI ID": method.upiId || seller.upiId || "N/A",
            "PayPal Email": seller.paypalEmail || "N/A",
            "Bank Holder Name": method.bankHolderName || bank.accountHolderName || "N/A",
            "Bank Name": method.bankName || bank.bankName || "N/A",
            "Account Number": method.bankAccountNumber || bank.accountNumber || "N/A",
            "IFSC Code": method.ifscCode || bank.ifscCode || "N/A",
          };
        });

      // 3. Document Details Sheet
      const docDetailsData = allSellers.map((seller, index) => ({
        "S.No": index + 1,
        "Shop Name": seller.name,
        "PAN Number": seller.panNumber || "N/A",
        "Trade License Count": seller.tradeLicenses?.length || 0,
        "Identity Proof URL": seller.identityProof?.url || "N/A",
        "Business Proof URL": seller.businessProof?.url || "N/A",
      }));

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Append sheets
      const sellersSheet = XLSX.utils.json_to_sheet(sellersData);
      XLSX.utils.book_append_sheet(workbook, sellersSheet, "Vendors Overview");

      if (bankDetailsData.length > 0) {
        const bankSheet = XLSX.utils.json_to_sheet(bankDetailsData);
        XLSX.utils.book_append_sheet(workbook, bankSheet, "Payment Methods");
      }

      const docSheet = XLSX.utils.json_to_sheet(docDetailsData);
      XLSX.utils.book_append_sheet(workbook, docSheet, "Verification Docs");

      // Generate filename with date
      const date = new Date();
      const filename = `Vendors_Complete_Export_${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}.xlsx`;

      // Save file
      XLSX.writeFile(workbook, filename);

      toast.success(
        `Successfully exported ${allSellers.length} vendors to Excel`
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export vendor data");
    }
  };

  // Local filtering based on search query
  const filteredSellers = sellers.filter(
    (seller) =>
      seller.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.country?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && sellers.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader />
      </div>
    );
  }

  return (
    <div className="w-full p-4 800px:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-rose-600 rounded-lg flex items-center justify-center">
            <AiOutlineShop className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl 800px:text-3xl font-bold text-gray-900">
              Vendor Management
            </h1>
            <p className="text-gray-600">
              Monitor registered sellers, review credentials, handle bans and export vendor details
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 400px:grid-cols-2 800px:grid-cols-4 gap-4 800px:gap-6 mb-6">
        {/* Total Vendors */}
        <div className={`${styles.card} ${styles.card_padding}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Vendors</p>
              <p className="text-2xl font-bold text-primary-600">{stats.total}</p>
              <div className="flex items-center mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  Registered Shops
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <AiOutlineShop className="h-6 w-6 text-primary-600" />
            </div>
          </div>
        </div>

        {/* Active Vendors */}
        <div className={`${styles.card} ${styles.card_padding}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Vendors</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              <div className="flex items-center mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <MdCheckCircle className="mr-1" size={12} />
                  Approved
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <MdCheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Banned Vendors */}
        <div className={`${styles.card} ${styles.card_padding}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Banned Vendors</p>
              <p className="text-2xl font-bold text-red-600">{stats.banned}</p>
              <div className="flex items-center mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <MdBlock className="mr-1" size={12} />
                  Suspended
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <MdBlock className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        {/* Pending Approval */}
        <div className={`${styles.card} ${styles.card_padding}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Approval</p>
              <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
              <div className="flex items-center mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  <FiInfo className="mr-1" size={12} />
                  Awaiting Review
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <FiInfo className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Action Bar */}
      <div className={`${styles.card} p-4 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between`}>
        <div className="relative w-full md:w-96">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search by shop name, email, or country..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.input}
            style={{ paddingLeft: "2.5rem" }}
          />
        </div>

        {/* Export Button */}
        <button
          onClick={exportToExcel}
          className="w-full md:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-all duration-200 shadow-md"
          title="Export all vendors to Excel"
        >
          <SiMicrosoftexcel size={16} />
          <span>Export to Excel</span>
        </button>
      </div>

      {/* Vendors Card Grid */}
      <div className={`${styles.card} overflow-hidden p-6`}>
        <div className="flex items-center justify-between pb-6 border-b border-gray-150 mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
              <AiOutlineShop className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Registered Vendors</h2>
              <p className="text-sm text-gray-500">
                {filteredSellers.length} vendor{filteredSellers.length !== 1 ? "s" : ""} listed on this page
              </p>
            </div>
          </div>
        </div>

        {filteredSellers.length === 0 ? (
          <div className="text-center py-12">
            <AiOutlineShop className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No vendors found</h3>
            <p className="mt-1 text-sm text-gray-500">Try adjusting your search terms.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSellers.map((item) => (
              <div
                key={item._id}
                onClick={() => openDetailsModal(item)}
                className="bg-white rounded-xl border border-gray-250 p-5 shadow-sm hover:shadow-md hover:border-primary-300 hover:-translate-y-0.5 transition-all duration-250 cursor-pointer flex flex-col justify-between group h-full"
              >
                <div>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="relative flex-shrink-0">
                      <div className="w-14 h-14 rounded-full overflow-hidden border border-gray-100 group-hover:border-primary-400 transition-colors duration-250 bg-gray-50 flex items-center justify-center">
                        <img
                          src={getAvatarUrl(item.avatar)}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><circle cx='12' cy='12' r='12' fill='%23FFE4E6'/><path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' fill='%23F43F5E'/></svg>";
                          }}
                        />
                      </div>
                      <div
                        className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center ${
                          item.isBanned ? "bg-red-500" : "bg-green-500"
                        }`}
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate group-hover:text-primary-600 transition-colors duration-200">
                        {item.name}
                      </h3>
                      <p className="text-xs text-gray-500 truncate">{item.email}</p>
                      {item.businessName && (
                        <p className="text-xs text-gray-400 font-medium truncate mt-0.5">
                          {item.businessName}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Address and Stats */}
                  <div className="space-y-2 mb-4 border-t border-gray-100 pt-3 text-xs text-gray-600">
                    <div className="flex items-center space-x-2">
                      <FiMapPin className="text-gray-400 flex-shrink-0" />
                      <span className="truncate">
                        {item.city ? `${item.city}, ` : ""}
                        {item.country || "India"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <FiDollarSign className="text-gray-400 flex-shrink-0" />
                        <span>
                          Revenue:{" "}
                          <strong className="text-gray-800 font-semibold">
                            {formatPrice(item.totalRevenue || 0)}
                          </strong>
                        </span>
                      </div>
                      <div className="text-[10px] bg-green-50 text-green-700 px-2 py-0.5 rounded border border-green-100">
                        Available: {formatPrice(item.dynamicAvailableBalance || 0)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer of Card */}
                <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                  <div className="flex space-x-1.5">
                    {/* Ban Badge */}
                    {item.isBanned && (
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-red-100 text-red-700 rounded-full flex items-center uppercase tracking-wider">
                        Banned
                      </span>
                    )}
                    {/* Status Badge */}
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                        item.approvalStatus === "approved"
                          ? "bg-green-100 text-green-700"
                          : item.approvalStatus === "pending"
                          ? "bg-orange-100 text-orange-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {item.approvalStatus || "pending"}
                    </span>
                  </div>
                  <span className="text-[10px] text-gray-400 flex items-center">
                    <FiCalendar className="mr-1" />
                    Joined {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-150 pt-6 mt-6 flex-col sm:flex-row gap-4">
            <div className="text-sm text-gray-500">
              Showing page <span className="font-semibold text-gray-800">{currentPage}</span> of{" "}
              <span className="font-semibold text-gray-800">{totalPages}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all duration-200 ${
                  currentPage === 1
                    ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                }`}
              >
                Previous
              </button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                  .filter((page) => {
                    return (
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 1
                    );
                  })
                  .map((page, idx, arr) => {
                    const showEllipsisBefore = idx > 0 && page - arr[idx - 1] > 1;
                    return (
                      <React.Fragment key={page}>
                        {showEllipsisBefore && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`w-9 h-9 rounded-lg text-sm font-medium transition-all duration-200 ${
                            currentPage === page
                              ? "bg-primary-600 text-white shadow-md"
                              : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all duration-200 ${
                  currentPage === totalPages
                    ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Vendor Details Modal */}
      {detailsModalOpen && selectedSeller && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
            detailsModalAnimating ? "bg-black bg-opacity-50" : "bg-black bg-opacity-0"
          }`}
        >
          <div
            className={`bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden transition-all duration-300 transform ${
              detailsModalAnimating
                ? "opacity-100 scale-100 translate-y-0"
                : "opacity-0 scale-95 translate-y-4"
            }`}
          >
            {/* Header Banner */}
            <div className="relative bg-gradient-to-r from-orange-500 to-rose-600 h-32 flex items-center justify-center px-6">
              <button
                onClick={closeDetailsModal}
                className="absolute top-4 right-4 p-1.5 bg-black bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-all duration-200"
              >
                <RxCross1 size={16} />
              </button>
              <div className="absolute -bottom-10 left-6">
                <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-md bg-white">
                  <img
                    src={getAvatarUrl(selectedSeller.avatar)}
                    alt={selectedSeller.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><circle cx='12' cy='12' r='12' fill='%23FFE4E6'/><path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' fill='%23F43F5E'/></svg>";
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Profile Content */}
            <div className="pt-12 px-6 pb-6 max-h-[calc(100vh-10rem)] overflow-y-auto">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedSeller.name}</h2>
                  <p className="text-sm text-gray-500">{selectedSeller.email}</p>
                </div>
                <div className="flex flex-col items-start md:items-end space-y-1 mt-2 md:mt-0">
                  <span
                    className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider ${
                      selectedSeller.approvalStatus === "approved"
                        ? "bg-green-100 text-green-700"
                        : selectedSeller.approvalStatus === "pending"
                        ? "bg-orange-100 text-orange-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    Approval: {selectedSeller.approvalStatus || "pending"}
                  </span>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center space-x-1 ${
                      selectedSeller.isBanned ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                    }`}
                  >
                    {selectedSeller.isBanned ? "Suspended" : "Active / Unbanned"}
                  </span>
                </div>
              </div>

              {/* Vendor Metadata */}
              <div className="border-t border-gray-100 pt-4 mb-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Shop Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <span className="block text-xs text-gray-400">Business / Owner Name</span>
                    <span className="text-gray-800 font-medium">{selectedSeller.businessName || "Not provided"}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-gray-400">Phone Number</span>
                    <span className="text-gray-800 font-medium">{selectedSeller.phoneNumber || "Not provided"}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-gray-400">Zip Code</span>
                    <span className="text-gray-800 font-medium">{selectedSeller.zipCode || "N/A"}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-gray-400">Joined Date</span>
                    <span className="text-gray-800 font-medium">
                      {selectedSeller.createdAt
                        ? new Date(selectedSeller.createdAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        : "N/A"}
                    </span>
                  </div>
                  <div className="md:col-span-2">
                    <span className="block text-xs text-gray-400">Full Address</span>
                    <span className="text-gray-800 font-medium">
                      {selectedSeller.address
                        ? `${selectedSeller.address}, ${selectedSeller.city || ""}, ${selectedSeller.state || ""}, ${
                            selectedSeller.country || "India"
                          }`
                        : "Not provided"}
                    </span>
                  </div>
                  {selectedSeller.description && (
                    <div className="md:col-span-2">
                      <span className="block text-xs text-gray-400">Shop Description</span>
                      <p className="text-gray-700 mt-1 italic">{selectedSeller.description}</p>
                    </div>
                  )}
                </div>

                {selectedSeller.isBanned && selectedSeller.banReason && (
                  <div className="p-3 bg-red-50 border border-red-100 rounded-lg mb-4">
                    <span className="block text-xs text-red-800 font-bold mb-1">Ban Reason</span>
                    <p className="text-sm text-red-700">{selectedSeller.banReason}</p>
                  </div>
                )}
              </div>

              {/* Financial Status */}
              <div className="border-t border-gray-100 pt-4 mb-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Financial Status</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 border border-gray-150 p-4 rounded-xl flex items-center justify-between">
                    <div>
                      <span className="text-xs text-gray-500 block">Total Revenue</span>
                      <span className="text-xl font-bold text-gray-800">
                        {formatPrice(selectedSeller.totalRevenue || 0)}
                      </span>
                    </div>
                  </div>
                  <div className="bg-green-50 border border-green-150 p-4 rounded-xl flex items-center justify-between">
                    <div>
                      <span className="text-xs text-green-700 block">Available Balance</span>
                      <span className="text-xl font-bold text-green-900">
                        {formatPrice(selectedSeller.dynamicAvailableBalance || 0)}
                      </span>
                    </div>
                    {selectedSeller.dynamicAvailableBalance > 0 && (
                      <button 
                        onClick={() => setWithdrawModalOpen(true)}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs font-bold shadow-sm"
                      >
                        Process Payout
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Saved Withdrawal Methods */}
              <div className="border-t border-gray-100 pt-4 mb-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Withdrawal Accounts</h4>
                <div className="space-y-3 text-sm">

                  {/* Bank/UPI Method */}
                  {selectedSeller.withdrawMethod ? (
                    <div className="p-3 bg-gray-50 border border-gray-250 rounded-lg">
                      <h5 className="font-semibold text-gray-800 mb-2">Primary Bank Details</h5>
                      <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs">
                        <div>
                          <span className="text-gray-400 block">Bank Name</span>
                          <strong>{selectedSeller.withdrawMethod.bankName}</strong>
                        </div>
                        <div>
                          <span className="text-gray-400 block">Holder Name</span>
                          <strong>{selectedSeller.withdrawMethod.bankHolderName}</strong>
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-400 block">Account Number</span>
                          <strong className="font-mono">{selectedSeller.withdrawMethod.bankAccountNumber}</strong>
                        </div>
                        <div>
                          <span className="text-gray-400 block">IFSC / Routing Code</span>
                          <strong className="font-mono">{selectedSeller.withdrawMethod.ifscCode}</strong>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">No bank withdrawal method configured.</p>
                  )}
                </div>
              </div>

              {/* Uploaded Documents & License */}
              <div className="border-t border-gray-100 pt-4 mb-6">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Verification Documents</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="text-gray-400 block mb-0.5">PAN Card Number</span>
                    <strong className="font-mono text-sm text-gray-800">{selectedSeller.panNumber || "Not Provided"}</strong>
                  </div>

                  {/* Trade Licenses */}
                  <div className="md:col-span-2">
                    <span className="text-gray-400 block mb-2">Trade Licenses ({selectedSeller.tradeLicenses?.length || 0})</span>
                    {selectedSeller.tradeLicenses && selectedSeller.tradeLicenses.length > 0 ? (
                      <div className="space-y-1.5 max-h-36 overflow-y-auto">
                        {selectedSeller.tradeLicenses.map((lic, idx) => (
                          <a
                            key={idx}
                            href={lic.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-2 border border-gray-200 hover:bg-gray-50 rounded-lg text-primary-600 transition-colors"
                          >
                            <span className="truncate text-gray-700 flex items-center">
                              <FiFileText className="mr-1.5 text-gray-400" />
                              {lic.originalName || `License #${idx + 1}`}
                            </span>
                            <span className="text-[10px] font-bold bg-primary-50 px-2 py-0.5 rounded text-primary-700 hover:underline">
                              View Document
                            </span>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="italic text-gray-400">No trade licenses uploaded.</p>
                    )}
                  </div>

                  {/* Identity Proof */}
                  <div>
                    <span className="text-gray-400 block mb-1">Identity Proof</span>
                    {selectedSeller.identityProof?.url ? (
                      <a
                        href={selectedSeller.identityProof.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center p-2 border border-gray-200 hover:bg-gray-50 rounded-lg text-primary-600 transition-colors"
                      >
                        <FiFileText className="mr-1.5 text-gray-400" />
                        <span className="truncate text-gray-700 flex-1">{selectedSeller.identityProof.originalName || "Identity Proof"}</span>
                        <span className="text-[10px] font-bold bg-primary-50 px-2 py-0.5 rounded text-primary-700">View</span>
                      </a>
                    ) : (
                      <span className="italic text-gray-400">Not Uploaded</span>
                    )}
                  </div>

                  {/* Business Proof */}
                  <div>
                    <span className="text-gray-400 block mb-1">Business Proof</span>
                    {selectedSeller.businessProof?.url ? (
                      <a
                        href={selectedSeller.businessProof.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center p-2 border border-gray-200 hover:bg-gray-50 rounded-lg text-primary-600 transition-colors"
                      >
                        <FiFileText className="mr-1.5 text-gray-400" />
                        <span className="truncate text-gray-700 flex-1">{selectedSeller.businessProof.originalName || "Business Proof"}</span>
                        <span className="text-[10px] font-bold bg-primary-50 px-2 py-0.5 rounded text-primary-700">View</span>
                      </a>
                    ) : (
                      <span className="italic text-gray-400">Not Uploaded</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="border-t border-gray-100 pt-4 flex space-x-3 justify-end">
                {selectedSeller.isBanned ? (
                  <button
                    onClick={() => {
                      closeDetailsModal();
                      setTimeout(() => openUnbanModal(selectedSeller), 350);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                  >
                    Unban Vendor
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      closeDetailsModal();
                      setTimeout(() => openBanModal(selectedSeller), 350);
                    }}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors"
                  >
                    Ban Vendor
                  </button>
                )}

                <button
                  onClick={() => {
                    closeDetailsModal();
                    setTimeout(() => openDeleteModal(selectedSeller._id), 350);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                >
                  Delete Shop
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Vendor Deletion</h3>
              <button
                onClick={closeDeleteModal}
                className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
              >
                <RxCross1 size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FiTrash2 className="text-red-600 h-6 w-6" />
              </div>
              <p className="text-gray-650 text-center text-sm">
                Are you sure you want to delete this vendor account? This will permanently delete the shop, all its coupons, events, and products. This action cannot be undone.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={closeDeleteModal}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSeller}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors shadow-sm"
              >
                Delete Shop
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ban Vendor Modal */}
      {banModalOpen && selectedSeller && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
            banModalAnimating ? "bg-black bg-opacity-50" : "bg-black bg-opacity-0"
          }`}
        >
          <div
            className={`bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4 transition-all duration-300 transform ${
              banModalAnimating
                ? "opacity-100 scale-100 translate-y-0"
                : "opacity-0 scale-95 translate-y-4"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Ban Shop Account</h3>
              <button
                onClick={closeBanModal}
                className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
              >
                <RxCross1 size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="mb-6">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FiShieldOff className="text-orange-600 h-6 w-6" />
              </div>
              <p className="text-gray-650 text-center text-sm mb-4">
                You are about to suspend <strong>{selectedSeller.name}</strong>. Please provide a clear reason for the suspension.
              </p>

              <textarea
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="Enter reason for banning this vendor..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm resize-none"
                rows={4}
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">{banReason.length}/500 characters</p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={closeBanModal}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBanSeller}
                disabled={!banReason.trim()}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Ban Vendor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unban Vendor Modal */}
      {unbanModalOpen && selectedSeller && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
            unbanModalAnimating ? "bg-black bg-opacity-50" : "bg-black bg-opacity-0"
          }`}
        >
          <div
            className={`bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4 transition-all duration-300 transform ${
              unbanModalAnimating
                ? "opacity-100 scale-100 translate-y-0"
                : "opacity-0 scale-95 translate-y-4"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Unban Shop Account</h3>
              <button
                onClick={closeUnbanModal}
                className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
              >
                <RxCross1 size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FiShield className="text-green-600 h-6 w-6" />
              </div>
              <p className="text-gray-655 text-center text-sm">
                Are you sure you want to lift the suspension for <strong>{selectedSeller.name}</strong>? They will be allowed to log back in and sell.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={closeUnbanModal}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUnbanSeller}
                className="flex-1 px-4 py-2 bg-green-650 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors shadow-sm"
              >
                Restore Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Payout Modal */}
      {withdrawModalOpen && selectedSeller && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 transition-all duration-300">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden transform">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">Process Manual Payout</h3>
              <button onClick={() => setWithdrawModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <RxCross1 size={20} />
              </button>
            </div>
            <form onSubmit={handleAdminWithdraw} className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Vendor: <strong className="text-gray-900">{selectedSeller.name}</strong>
                </p>
                <div className="bg-green-50 p-3 rounded-lg border border-green-100 mb-4 flex justify-between items-center">
                  <span className="text-sm text-green-800 font-medium">Available Balance:</span>
                  <span className="font-bold text-green-700">{formatPrice(selectedSeller.dynamicAvailableBalance || 0)}</span>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount to Pay Out (₹)</label>
                <input
                  type="number"
                  required
                  min="1"
                  max={selectedSeller.dynamicAvailableBalance}
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                  placeholder="Enter amount..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setWithdrawModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={withdrawLoading || !withdrawAmount}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium flex items-center transition-colors"
                >
                  {withdrawLoading ? "Processing..." : "Confirm Payout"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllSellers;
